/**
 * useProfileQuery - Hook para dados do perfil do usuário
 * 
 * Encapsula chamadas ao storageService com cache do React Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Platform } from 'react-native';
import { supabase } from '../../lib/supabase';
import { getCachedUser } from '../../services/userCacheService';
import {
    getProfileScreen,
    getProfileDataOptimized,
    getAchievementsForProfileOptimized,
    getUserActivitiesOptimized,
    getActiveGoalOptimized,
    getWeeklyStatsFromDB,
} from '../../services/databaseService';
import { getWeeklyStats } from '../../services/storageService';

// ==================== QUERY KEYS ====================
export const profileKeys = {
    all: ['profile'],
    user: (userId) => [...profileKeys.all, 'user', userId],
    stats: (userId) => [...profileKeys.all, 'stats', userId],
    current: (userId) => [...profileKeys.all, 'current', userId],
    data: (userId) => [...profileKeys.all, 'data', userId],
    screen: (userId) => [...profileKeys.all, 'screen', userId],
    achievements: (userId) => [...profileKeys.all, 'achievements', userId],
    activities: (userId) => [...profileKeys.all, 'activities', userId],
    goal: (userId) => [...profileKeys.all, 'goal', userId],
    weekly: (userId) => [...profileKeys.all, 'weekly', userId],
};

// ==================== FETCH FUNCTIONS ====================

/**
 * Busca dados do perfil do usuário atual
 */
const fetchCurrentProfile = async () => {
    const user = await getCachedUser();
    if (!user) return null;

    // Otimizado: colunas específicas
    const { data, error } = await supabase
        .from('profiles')
        .select('id, nome, username, avatar_seed, foto_url, bio, is_verified, is_premium, streak_atual, nivel, xp_total, questoes_respondidas, created_at')
        .eq('id', user.id)
        .single();

    if (error) throw error;
    return data;
};

/**
 * Busca dados de um perfil por ID
 */
const fetchProfileById = async (userId) => {
    // Otimizado: colunas específicas
    const { data, error } = await supabase
        .from('profiles')
        .select('id, nome, username, avatar_seed, foto_url, bio, is_verified, is_premium, streak_atual, nivel, xp_total, questoes_respondidas, created_at')
        .eq('id', userId)
        .single();

    if (error) throw error;
    return data;
};

/**
 * Busca estatísticas do usuário
 */
const fetchUserStats = async (userId) => {
    // Otimizado: colunas específicas
    const { data, error } = await supabase
        .from('user_statistics')
        .select('total_questions, correct_answers, accuracy, study_streak')
        .eq('user_id', userId)
        .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data || {
        total_questions: 0,
        correct_answers: 0,
        accuracy: 0,
        study_streak: 0,
    };
};

// ==================== HOOKS ====================

/**
 * Hook para buscar perfil do usuário atual
 */
export const useCurrentProfile = (userId, enabled = true) => {
    return useQuery({
        queryKey: profileKeys.current(userId),
        queryFn: fetchCurrentProfile,
        staleTime: 5 * 60 * 1000, // 5 minutos
        enabled: enabled && !!userId,
    });
};

/**
 * Hook para buscar perfil por ID
 */
export const useProfile = (userId, enabled = true) => {
    return useQuery({
        queryKey: profileKeys.user(userId),
        queryFn: () => fetchProfileById(userId),
        staleTime: 5 * 60 * 1000, // 5 minutos
        enabled: enabled && !!userId,
    });
};

/**
 * Hook para buscar estatísticas do usuário
 */
export const useUserStats = (userId, enabled = true) => {
    return useQuery({
        queryKey: profileKeys.stats(userId),
        queryFn: () => fetchUserStats(userId),
        staleTime: 2 * 60 * 1000, // 2 minutos (stats mudam mais)
        enabled: enabled && !!userId,
    });
};

/**
 * Hook ÚNICO da tela de perfil: 1 RPC (1 round-trip) agregando tudo
 * ({ userData, stats, achievements, activities, goal, weekly }).
 * É a fonte que a ProfileScreen consome — substitui os 5 hooks granulares abaixo.
 */
export const useProfileScreen = (userId, enabled = true) => {
    return useQuery({
        queryKey: profileKeys.screen(userId),
        queryFn: () => getProfileScreen(userId),
        staleTime: 5 * 60 * 1000,
        // WEB: esta é a ÚNICA query da allowlist de persistência (ver
        // `QueryProvider`). O `gcTime` global da web é 30 min — curto de
        // propósito, para a SPA não acumular feeds e vídeos durante a sessão —
        // mas o cache persistido vale 24h, e o provider documenta o requisito
        // `gcTime >= maxAge`: com 30 min ela seria coletada antes de poder ser
        // reidratada e a persistência não serviria para nada. Estender só aqui
        // custa um objeto pequeno na memória, não a SPA inteira.
        // NATIVO: `undefined` mantém o padrão global (30 dias), inalterado.
        gcTime: Platform.OS === 'web' ? 1000 * 60 * 60 * 24 : undefined,
        enabled: enabled && !!userId,
    });
};

/**
 * Hook agregado do perfil (mesmo shape que getProfileDataOptimized: { userData, stats })
 */
export const useProfileData = (userId, enabled = true) => {
    return useQuery({
        queryKey: profileKeys.data(userId),
        queryFn: () => getProfileDataOptimized(userId),
        staleTime: 5 * 60 * 1000,
        enabled: enabled && !!userId,
    });
};

/**
 * Hook para conquistas do perfil (formato da ProfileScreen)
 */
export const useProfileAchievements = (userId, enabled = true) => {
    return useQuery({
        queryKey: profileKeys.achievements(userId),
        queryFn: () => getAchievementsForProfileOptimized(userId),
        staleTime: 5 * 60 * 1000,
        enabled: enabled && !!userId,
    });
};

/**
 * Hook para atividades recentes do perfil
 */
export const useProfileActivities = (userId, limit = 5, enabled = true) => {
    return useQuery({
        queryKey: [...profileKeys.activities(userId), limit],
        queryFn: () => getUserActivitiesOptimized(userId, limit),
        staleTime: 2 * 60 * 1000,
        enabled: enabled && !!userId,
    });
};

/**
 * Hook para a meta ativa do usuário
 */
export const useActiveGoal = (userId, enabled = true) => {
    return useQuery({
        queryKey: profileKeys.goal(userId),
        queryFn: () => getActiveGoalOptimized(userId),
        staleTime: 5 * 60 * 1000,
        enabled: enabled && !!userId,
    });
};

/**
 * Hook para estatísticas semanais (DB com fallback local)
 */
export const useWeeklyStats = (userId, enabled = true) => {
    return useQuery({
        queryKey: profileKeys.weekly(userId),
        queryFn: async () => {
            const fromDb = await getWeeklyStatsFromDB();
            if (fromDb) return fromDb;
            return await getWeeklyStats();
        },
        staleTime: 5 * 60 * 1000,
        enabled: enabled && !!userId,
    });
};

// ==================== MUTATIONS ====================

/**
 * Hook para atualizar perfil
 */
export const useUpdateProfile = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (updates) => {
            const user = await getCachedUser();
            if (!user) throw new Error('Usuário não autenticado');

            // Colunas explícitas: select('*')/select() em profiles falha desde a
            // migration 20260610130000 (grant por coluna — correção #2). Mesma
            // lista de fetchCurrentProfile p/ manter o shape do cache.
            const { data, error } = await supabase
                .from('profiles')
                .update(updates)
                .eq('id', user.id)
                .select('id, nome, username, avatar_seed, foto_url, bio, is_verified, is_premium, streak_atual, nivel, xp_total, questoes_respondidas, created_at')
                .single();

            if (error) throw error;
            return data;
        },
        onSuccess: (data) => {
            // Atualiza cache do perfil atual
            queryClient.setQueryData(profileKeys.current(), data);
            queryClient.invalidateQueries({ queryKey: profileKeys.user(data.id) });
        },
    });
};
